import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Star, Sparkles, Scissors, Flower2, Heart, ArrowRight, Instagram, Quote } from "lucide-react";
import useReveal from "../hooks/useReveal";
import { BRAND, SERVICE_CATEGORIES, GALLERY_ITEMS, BEFORE_AFTER, WHY_CHOOSE_US, INSTAGRAM_FEED } from "../lib/data";
import { getTestimonials } from "../lib/api";
import BeforeAfter from "../components/BeforeAfter";

const iconMap = { Sparkles, Scissors, Flower2, Heart };

export default function Home() {
  useReveal();
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    getTestimonials().then(setTestimonials).catch(() => setTestimonials([]));
  }, []);

  return (
    <div data-testid="home-page" className="bg-[#0B0B0B] text-white">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-28 pb-20 px-6 md:px-12 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.unsplash.com/photo-1560066984-138dadb4c035?w=1800&q=80"
            alt="salon hero"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0B]/70 via-[#0B0B0B]/85 to-[#0B0B0B]" />
        </div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 reveal">
            <span className="gold-divider text-[11px] tracking-[0.32em] uppercase">Est. Pendle Hill</span>
            <h1 className="hero-title font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.05] text-white">
              Luxury Hair & Beauty
              <br />
              <span className="italic text-[#C9A34E]">Salon in Pendle Hill</span>
            </h1>
            <p className="mt-7 text-base md:text-lg text-white/75 max-w-2xl leading-relaxed">
              Hair • Colour • Highlights • Keratin • Nanoplasty • Facials • Waxing • Threading • Makeup
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link
                to="/booking"
                data-testid="hero-book-btn"
                className="group inline-flex items-center gap-2 bg-[#C9A34E] text-[#0B0B0B] hover:bg-[#b08d3e] rounded-full px-8 py-3.5 text-sm tracking-[0.2em] uppercase font-medium transition-all"
              >
                Book Appointment <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-services-btn"
                className="inline-flex items-center gap-2 border border-[#C9A34E] text-[#C9A34E] hover:bg-[#C9A34E] hover:text-[#0B0B0B] rounded-full px-8 py-3.5 text-sm tracking-[0.2em] uppercase font-medium transition-colors"
              >
                View Services
              </Link>
            </div>

            <div className="flex items-center gap-6 mt-12 text-sm text-white/70">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => <Star key={i} size={16} className="text-[#C9A34E] fill-[#C9A34E]" />)}
              </div>
              <span className="tracking-wider">500+ Happy Clients</span>
            </div>
          </div>

          <div className="lg:col-span-5 reveal flex justify-center">
            <div className="relative animate-float-soft">
              <div className="absolute -inset-6 rounded-full bg-[#C9A34E]/10 blur-3xl" />
              <img
                src={BRAND.logo}
                alt="Enrich Hair & Beauty Logo"
                className="relative w-72 h-72 md:w-96 md:h-96 object-cover rounded-full ring-1 ring-[#C9A34E]/40 shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section data-testid="why-section" className="py-24 md:py-32 px-6 md:px-12 bg-[#0B0B0B]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center reveal">
            <span className="gold-divider text-[11px] tracking-[0.32em] uppercase">Why Choose Us</span>
            <h2 className="font-display text-4xl md:text-5xl mt-5">The Enrich Difference</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
            {WHY_CHOOSE_US.map((w, i) => {
              const Icon = iconMap[w.icon] || Sparkles;
              return (
                <div
                  key={w.title}
                  data-testid={`why-card-${i}`}
                  className="reveal card-shimmer bg-[#141414] border border-[#C9A34E]/15 rounded-2xl p-8 hover:-translate-y-1 hover:border-[#C9A34E]/50 transition-all"
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  <div className="w-12 h-12 rounded-full bg-[#C9A34E]/10 border border-[#C9A34E]/30 flex items-center justify-center text-[#C9A34E] mb-5">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-display text-xl text-white">{w.title}</h3>
                  <p className="text-sm text-white/65 mt-3 leading-relaxed">{w.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section data-testid="services-preview" className="py-24 md:py-32 px-6 md:px-12 bg-[#0E0E0E]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 reveal">
            <div>
              <span className="gold-divider text-[11px] tracking-[0.32em] uppercase">Our Menu</span>
              <h2 className="font-display text-4xl md:text-5xl mt-5">Signature Services</h2>
            </div>
            <Link to="/services" data-testid="see-all-services" className="text-[#C9A34E] text-sm tracking-[0.2em] uppercase hover:underline">View Full Menu →</Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {SERVICE_CATEGORIES.map((c, i) => (
              <Link
                key={c.id}
                to="/services"
                data-testid={`service-card-${c.id}`}
                className="reveal group relative overflow-hidden rounded-2xl border border-[#C9A34E]/15 aspect-[3/4]"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B]/40 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-6">
                  <h3 className="font-display text-2xl text-white">{c.title}</h3>
                  <p className="text-[11px] tracking-[0.25em] uppercase text-[#C9A34E] mt-2">{c.items.length} treatments</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section data-testid="before-after-section" className="py-24 md:py-32 px-6 md:px-12 bg-[#0B0B0B]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center reveal">
            <span className="gold-divider text-[11px] tracking-[0.32em] uppercase">Transformations</span>
            <h2 className="font-display text-4xl md:text-5xl mt-5">Before & After</h2>
            <p className="text-white/65 max-w-xl mx-auto mt-4 text-sm">Slide to reveal the Enrich touch — real results from our team.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-14">
            {BEFORE_AFTER.map((ba, i) => (
              <div key={ba.title} className="reveal" style={{ transitionDelay: `${i * 100}ms` }}>
                <BeforeAfter {...ba} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section data-testid="gallery-preview" className="py-24 md:py-32 px-6 md:px-12 bg-[#0E0E0E]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 reveal">
            <div>
              <span className="gold-divider text-[11px] tracking-[0.32em] uppercase">Looks We Love</span>
              <h2 className="font-display text-4xl md:text-5xl mt-5">From Our Chair</h2>
            </div>
            <Link to="/gallery" data-testid="see-all-gallery" className="text-[#C9A34E] text-sm tracking-[0.2em] uppercase hover:underline">Full Gallery →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-12">
            {GALLERY_ITEMS.slice(0, 8).map((g, i) => (
              <div key={i} className="gallery-card aspect-square reveal" style={{ transitionDelay: `${i * 60}ms` }}>
                <img src={g.src} alt={g.title} className="w-full h-full object-cover" />
                <div className="overlay">
                  <p className="text-sm font-display text-[#C9A34E]">{g.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="py-24 md:py-32 px-6 md:px-12 bg-[#0B0B0B]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center reveal">
            <span className="gold-divider text-[11px] tracking-[0.32em] uppercase">Client Love</span>
            <h2 className="font-display text-4xl md:text-5xl mt-5">Stories From Our Guests</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {testimonials.map((t, i) => (
              <div
                key={t.id}
                data-testid={`testimonial-${i}`}
                className="reveal relative bg-gradient-to-br from-[#1A1A1A] to-[#0E0E0E] border border-[#C9A34E]/30 rounded-2xl p-8"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <Quote className="absolute top-5 right-5 text-[#C9A34E]/30" size={36} />
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, j) => <Star key={j} size={16} className="text-[#C9A34E] fill-[#C9A34E]" />)}
                </div>
                <p className="text-white/85 leading-relaxed italic">"{t.text}"</p>
                <div className="mt-6 pt-5 border-t border-[#C9A34E]/15">
                  <p className="font-display text-lg text-white">{t.name}</p>
                  <p className="text-[11px] tracking-[0.25em] uppercase text-[#C9A34E] mt-1">{t.service}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSTAGRAM FEED */}
      <section data-testid="instagram-section" className="py-24 md:py-32 px-6 md:px-12 bg-[#0E0E0E]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center reveal">
            <span className="gold-divider text-[11px] tracking-[0.32em] uppercase">Follow Along</span>
            <h2 className="font-display text-4xl md:text-5xl mt-5">@enrichhairbeauty</h2>
            <a href={BRAND.instagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-5 text-[#C9A34E] text-sm tracking-[0.2em] uppercase hover:underline">
              <Instagram size={16} /> Visit Instagram
            </a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-14">
            {INSTAGRAM_FEED.map((src, i) => (
              <a key={i} href={BRAND.instagram} target="_blank" rel="noreferrer" data-testid={`ig-${i}`} className="gallery-card aspect-square reveal" style={{ transitionDelay: `${i * 50}ms` }}>
                <img src={src} alt="instagram" className="w-full h-full object-cover" />
                <div className="overlay flex items-center justify-center">
                  <Instagram size={26} className="text-[#C9A34E]" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 md:px-12 bg-[#0B0B0B]">
        <div className="max-w-5xl mx-auto reveal text-center border border-[#C9A34E]/25 rounded-3xl p-12 md:p-16 bg-gradient-to-br from-[#141414] to-[#0B0B0B]">
          <h2 className="font-display text-3xl md:text-5xl">Ready for your <span className="italic text-[#C9A34E]">Enrich</span> moment?</h2>
          <p className="text-white/65 mt-5 max-w-xl mx-auto">Book your appointment online — we'll take care of the rest in our serene Pendle Hill studio.</p>
          <Link to="/booking" data-testid="cta-book-btn" className="inline-flex items-center gap-2 mt-8 bg-[#C9A34E] text-[#0B0B0B] hover:bg-[#b08d3e] rounded-full px-9 py-4 text-sm tracking-[0.2em] uppercase font-medium transition-colors">
            Book Now <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
